import { FC, useEffect, useState } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { FormContainer, TextFieldElement } from "react-hook-form-mui"
import {
  Box,
  Button,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { Add, Close } from "@mui/icons-material"
import { Modal } from "@/core/components"
import { getApiErrorMessage } from "@/core/utils"
import { store } from "@core/store"
import { RoleSelect } from "@/modules/userbot/components/common/roleSelect"
import { useLazyRolesQuery } from "@/modules/userbot/store"
import { guestBotApi, useProvisionCreateMutation } from "../../store"
import {
  getProvisionErrorText,
  isProvisionLocalError,
  isValidBotUsername,
} from "../../utils/provisionStatus"
import { ProvisionLocalError } from "./types"

interface IProps {
  onClose: () => void
  onLaunched: (
    localErrors: ProvisionLocalError[],
    launchedCount: number,
  ) => void
}

interface BotRow {
  title: string
  username: string
}
interface FormValues {
  bots: BotRow[]
  role: string
  delaySeconds: number
}

const MAX_BOTS = 10
const USERNAME_MESSAGE = "5–32 символа: латиница/цифры/_, на конце «bot»"

/** Модалка массового создания ботов через BotFather (спека §4). */
/**
 * Внутренняя часть формы: всё состояние живёт здесь.
 * Core `Modal` создаёт обёртку элемента внутри своего рендера — любой
 * ре-рендер родителя Modal пересоздаёт поддерево (remount). Поэтому
 * ProvisionBotForm держится render-stable (только props), а состояние — в Inner.
 */
const ProvisionBotFormInner: FC<Pick<IProps, "onLaunched">> = ({
  onLaunched,
}) => {
  const [provisionCreate, { isLoading }] = useProvisionCreateMutation()
  const [fetchRoles] = useLazyRolesQuery()
  const form = useForm<FormValues>({
    defaultValues: {
      bots: [{ title: "", username: "" }],
      role: "",
      delaySeconds: 0,
    },
    mode: "onChange",
  })
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "bots",
  })
  // Настройки вне RHF: карта тогглов 0/1, «Гостевой режим» включён по умолчанию.
  const [settings, setSettings] = useState<Record<string, 0 | 1>>({ gst: 1 })
  const [newKey, setNewKey] = useState("")

  // Дефолт роли: guest-bot, только если такая роль есть в списке (спека §4.1).
  useEffect(() => {
    fetchRoles({ limit: 100, offset: 0 })
      .then((res) => {
        if (res.data?.items.some((r) => r.name === "guest-bot")) {
          form.setValue("role", "guest-bot")
        }
      })
      .catch(() => undefined)
  }, [fetchRoles, form])

  const usernameRules = (index: number) => ({
    required: "Обязательное поле",
    validate: (v: string) => {
      if (!isValidBotUsername(v ?? "")) return USERNAME_MESSAGE
      const dup = (form.getValues("bots") ?? []).some(
        (b, j) =>
          j !== index &&
          (b.username ?? "").toLowerCase() === (v ?? "").toLowerCase(),
      )
      return !dup || "Такой юзернейм уже добавлен"
    },
  })

  const addKey = () => {
    const key = newKey.trim()
    if (!key || key in settings) return
    setSettings((prev) => ({ ...prev, [key]: 0 }))
    setNewKey("")
  }

  const onSubmit = async (values: FormValues) => {
    const localErrors: ProvisionLocalError[] = []
    let launched = 0
    for (const [i, row] of values.bots.entries()) {
      try {
        await provisionCreate({
          body: {
            title: row.title.trim(),
            username: row.username.trim(),
            settings,
            delaySeconds: Number(values.delaySeconds) || 0,
            ...(values.role ? { role: values.role } : {}),
          },
        }).unwrap()
        launched += 1
      } catch (e) {
        if (isProvisionLocalError(e)) {
          localErrors.push({
            key: `local-${Date.now()}-${i}`,
            title: row.title.trim(),
            username: row.username.trim(),
            text: getProvisionErrorText(null, getApiErrorMessage(e)),
          })
        } else {
          // 500/409: серверная запись остаётся — красный чип придёт списком.
          store.dispatch(guestBotApi.util.invalidateTags(["Provisions"]))
        }
      }
    }
    onLaunched(localErrors, launched)
  }

  return (
    <FormContainer formContext={form} onSuccess={onSubmit}>
      <Stack spacing={2}>
        {fields.map((field, i) => (
          <Box key={field.id} display="flex" gap={1} alignItems="flex-start">
            <TextFieldElement
              name={`bots.${i}.title` as const}
              label="Имя"
              required
              fullWidth
              helperText="Видно в Telegram"
              validation={{
                validate: (v: string) => {
                  const trimmed = (v ?? "").trim()
                  if (!trimmed) return "Обязательное поле"
                  if (trimmed.length > 64) return "Максимум 64 символа"
                  return true
                },
              }}
            />
            <TextFieldElement
              name={`bots.${i}.username` as const}
              label="Юзернейм"
              required
              fullWidth
              validation={usernameRules(i)}
            />
            <Tooltip title="Удалить запись" arrow>
              <span>
                <IconButton
                  size="small"
                  aria-label="Удалить запись"
                  disabled={fields.length === 1}
                  onClick={() => remove(i)}
                >
                  <Close fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
        <Box>
          <Button
            size="small"
            startIcon={<Add />}
            disabled={fields.length >= MAX_BOTS}
            onClick={() => append({ title: "", username: "" })}
          >
            Добавить бота ({fields.length}/{MAX_BOTS})
          </Button>
        </Box>
        <RoleSelect<FormValues> name="role" label="Роль" required />
        <TextFieldElement
          name="delaySeconds"
          label="Задержка ответа (сек, 0–180)"
          type="number"
          fullWidth
          validation={{
            min: { value: 0, message: "Минимум 0" },
            max: { value: 180, message: "Максимум 180" },
          }}
        />
        <Box>
          <Typography variant="subtitle2" mb={1}>
            Настройки
          </Typography>
          <Stack spacing={1}>
            {Object.entries(settings).map(([key, value]) => (
              <Box
                key={key}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2">
                  {key === "gst" ? "Гостевой режим" : key}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.5 }}
                  >
                    ({key})
                  </Typography>
                </Typography>
                <Switch
                  checked={value === 1}
                  inputProps={{
                    "aria-label": key === "gst" ? "Гостевой режим" : key,
                  }}
                  onChange={(_, checked) =>
                    setSettings((prev) => ({ ...prev, [key]: checked ? 1 : 0 }))
                  }
                />
              </Box>
            ))}
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                size="small"
                label="Новый ключ"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
              <Button size="small" onClick={addKey} disabled={!newKey.trim()}>
                Добавить ключ
              </Button>
            </Box>
          </Stack>
        </Box>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading || !form.formState.isValid}
        >
          Создать ({fields.length})
        </Button>
      </Stack>
    </FormContainer>
  )
}

/** Публичная обёртка: render-stable родитель для Modal (см. комментарий к Inner). */
export const ProvisionBotForm: FC<IProps> = ({ onClose, onLaunched }) => (
  <Modal
    disableBackdropClose
    title="Создание ботов через BotFather"
    onClose={onClose}
    element={<ProvisionBotFormInner onLaunched={onLaunched} />}
  />
)
